import React from "react";
import { useClient } from "urql";
import yaml from "js-yaml";
import {
  CanbyAnalysisExportDocument,
  CanbyAnalysisExportQuery,
  CanbyAnalysisShowFragment,
} from "../../graphql/generated";

type ExportData = CanbyAnalysisExportQuery;
type ExportField = ExportData["canbyAnalysis"]["fields"][number];

function percentage(count: number, total: number): string {
  if (total === 0) return "0.00% • 0/0";
  return `${((count / total) * 100).toFixed(2)}% • ${count}/${total}`;
}

function buildFieldData(field: ExportField, sampleSize: number): Record<string, unknown> {
  const stats = field.statistics;
  const result: Record<string, unknown> = {
    name: field.name,
    usage: percentage(stats.fieldExists, sampleSize),
  };

  if (stats.boolean) {
    result.boolean = {
      percentage: percentage(stats.boolean.documents, stats.fieldExists),
      trues: percentage(stats.boolean.trueDocuments, stats.boolean.documents),
      falses: percentage(stats.boolean.falseDocuments, stats.boolean.documents),
    };
  }

  if (stats.date) {
    result.date = {
      percentage: percentage(stats.date.documents, stats.fieldExists),
      quartiles: [stats.date.min, stats.date.lowerQuartile, stats.date.median, stats.date.upperQuartile, stats.date.max],
      average: stats.date.average,
    };
  }

  if (stats.number) {
    result.number = {
      percentage: percentage(stats.number.documents, stats.fieldExists),
      quartiles: [stats.number.min, stats.number.lowerQuartile, stats.number.median, stats.number.upperQuartile, stats.number.max],
      average: stats.number.average,
    };
  }

  if (stats.timestamp) {
    result.timestamp = {
      percentage: percentage(stats.timestamp.documents, stats.fieldExists),
      quartiles: [stats.timestamp.min, stats.timestamp.lowerQuartile, stats.timestamp.median, stats.timestamp.upperQuartile, stats.timestamp.max],
      average: stats.timestamp.average,
    };
  }

  if (stats.keyword) {
    const kw: Record<string, unknown> = {
      percentage: percentage(stats.keyword.documents, stats.fieldExists),
      cardinality: stats.keyword.cardinality,
      "average length": stats.keyword.averageLength,
      "minimum length": stats.keyword.minLength,
      "maximum length": stats.keyword.maxLength,
    };
    if (field.keywords.length > 0) {
      kw.keywords = field.keywords.map((k) => ({
        keyword: k.keyword,
        usage: percentage(k.count, stats.keyword!.documents),
      }));
    }
    result.keyword = kw;
  }

  return result;
}

export function useCanbyAnalysisExport(analysis?: CanbyAnalysisShowFragment) {
  const client = useClient();
  const [exporting, setExporting] = React.useState(false);

  const exportYaml = React.useCallback(async () => {
    if (!analysis) return;
    setExporting(true);
    try {
      const result = await client
        .query<ExportData>(CanbyAnalysisExportDocument, { id: analysis.id })
        .toPromise();

      if (result.error || !result.data) {
        throw new Error(result.error?.message ?? "Failed to fetch analysis data");
      }

      const data = result.data.canbyAnalysis;
      const mlsId = data.rmRootResource?.mls.id ?? "unknown";
      const sampleSize = data.documentCount;
      const date = new Date().toISOString().split("T")[0];

      const sortedFields = [...data.fields].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));

      const output = {
        mls: mlsId,
        date,
        "sample size": sampleSize,
        fields: sortedFields.map((field) => buildFieldData(field, sampleSize)),
      };

      const yamlStr = yaml.dump(output, { lineWidth: -1, quotingType: '"', forceQuotes: false });
      const blob = new Blob([yamlStr], { type: "application/x-yaml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${mlsId}-canby-export-${date}.yaml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [analysis, client]);

  return { exportYaml, exporting };
}
